import BookingAssistantSettings from './pages/admin/BookingAssistantSettings';
import AdminFarmOuts from './pages/admin/AdminFarmOuts';
import React, { lazy, Suspense, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { StripeProvider } from './context/StripeContext';
import { BrandingProvider } from './context/BrandingContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import BookingPage from './pages/customer/BookingPage';
import ReservationsPage from './pages/customer/ReservationsPage';
import PaymentPage from './pages/customer/PaymentPage';
import BillingPage from './pages/customer/BillingPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminReservations from './pages/admin/AdminReservations';
import AdminQuotes from './pages/admin/AdminQuotes';
import AdminDispatch from './pages/admin/AdminDispatch';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminBilling from './pages/admin/AdminBilling';
import AdminSettings from './pages/admin/AdminSettings';
import AdminVehicles from './pages/admin/AdminVehicles';
import AdminDrivers from './pages/admin/AdminDrivers';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminInvoiceReplacements from './pages/admin/AdminInvoiceReplacements';
import ReplacementPaymentPage from './pages/public/ReplacementPaymentPage';
import AdminPayments from './pages/admin/AdminPayments';
import AdminUsers from './pages/admin/AdminUsers';
import AdminAccounts from './pages/admin/AdminAccounts';
import AdminAccountDetail from './pages/admin/AdminAccountDetail';
import AdminCompanies from './pages/admin/AdminCompanies';
import AdminOnboarding from './pages/admin/AdminOnboarding';
import AdminInvoiceImport from './pages/admin/AdminInvoiceImport';
import PlatformBilling from './pages/admin/PlatformBilling';
import PlatformUsers from './pages/admin/PlatformUsers';
import PlatformInvoices from './pages/admin/PlatformInvoices';
import PlatformSupportStaff from './pages/admin/PlatformSupportStaff';
import AiAccess from './pages/admin/AiAccess';
import OperatorAssistant from './pages/admin/OperatorAssistant';
import { OperatorAssistantProvider } from './components/operator-assistant/OperatorAssistantContext';
import OperatorAssistantDrawer from './components/operator-assistant/OperatorAssistantDrawer';
import SupportTenantPortal from './pages/admin/SupportTenantPortal';
import SupportTokenHandoffPage from './pages/SupportTokenHandoffPage';
import PaymentRequestPage from './pages/public/PaymentRequestPage';
import PublicInvoicePage from './pages/public/PublicInvoicePage';
import PublicQuotePage from './pages/public/PublicQuotePage';
import PublicReservationRecordPage from './pages/public/PublicReservationRecordPage';
import CustomerAccessPage from './pages/public/CustomerAccessPage';
import CustomerPortalPage from './pages/public/CustomerPortalPage';
import PaymentMethodSetupPage from './pages/public/PaymentMethodSetupPage';
import TripInquiryPage from './pages/public/TripInquiryPage';

// Components
import Navbar from './components/Navbar';
import PlatformSidebar from './components/PlatformSidebar';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';

const BookingChatPage = lazy(() => import('./pages/public/BookingChatPage'));
const OperationsWorkspace = lazy(() => import('./pages/operations/OperationsWorkspace'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const { effectiveTheme, themePreference } = useTheme();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const isPlatformHost =
    typeof window !== 'undefined' &&
    window.location?.hostname?.toLowerCase().startsWith('globaladmin.');

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const isInquirySurface = ['/reserve', '/inquire', '/booking-chat'].includes(
    location.pathname
  );
  const showSidebar = Boolean(
    !isInquirySurface &&
      user &&
      (user.role === 'admin' || (user as any).is_support_staff) &&
      (!(user as any).is_support_staff || !isPlatformHost)
  );
  const isAdminSurface = Boolean(
    user && (user.role === 'admin' || (user as any).is_support_staff)
  );

  return (
    <div
      className="min-h-screen bg-surface"
      data-admin-surface={isAdminSurface ? 'true' : 'false'}
      data-theme={effectiveTheme}
      data-theme-preference={themePreference}
    >
      {showSidebar && (
        <PlatformSidebar
          collapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed((current) => !current)}
        />
      )}

      {user && !isInquirySurface && (
        <Navbar
          hasSidebar={showSidebar}
          isSidebarCollapsed={isSidebarCollapsed}
        />
      )}

      <main>
        <div
          className={
            user && !isInquirySurface
              ? showSidebar
                ? isSidebarCollapsed
                  ? 'pt-16 md:pl-24'
                  : 'pt-16 md:pl-72'
                : 'pt-16'
              : ''
          }
        >
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={!user ? <LoginPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="/register"
              element={!user ? <RegisterPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="/reset-password"
              element={
                !user ? <ResetPasswordPage /> : <Navigate to="/" replace />
              }
            />

            <Route
              path="/pay/request/:token"
              element={<PaymentRequestPage />}
            />
            <Route path="/pay/replacement/:token" element={<ReplacementPaymentPage />} />
            <Route path="/pay/invoice/:token" element={<PublicInvoicePage />} />
            <Route path="/quote/:token" element={<PublicQuotePage />} />
            <Route
              path="/reservation/:token"
              element={<PublicReservationRecordPage />}
            />
            <Route
              path="/booking-chat"
              element={
                <Suspense
                  fallback={<div role="status">Opening booking assistant…</div>}
                >
                  <BookingChatPage />
                </Suspense>
              }
            />
            <Route
              path="/platform/booking-assistant"
              element={
                <ProtectedRoute>
                  <PlatformOnlyRoute>
                    <BookingAssistantSettings />
                  </PlatformOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/customer/access" element={<CustomerAccessPage />} />
            <Route path="/customer" element={<CustomerPortalPage />} />
          <Route path="/payment-method/setup/:token" element={<PaymentMethodSetupPage />} />
            <Route path="/reserve" element={<TripInquiryPage />} />
            <Route path="/inquire" element={<TripInquiryPage />} />

            <Route
              path="/support/handoff"
              element={<SupportTokenHandoffPage />}
            />

            {/* Protected customer routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  {(user as any)?.is_support_staff && isPlatformHost ? (
                    <Navigate to="/support/tenant-switcher" replace />
                  ) : user?.role === 'admin' ? (
                    <AdminDashboard />
                  ) : (user as any)?.is_support_staff ? (
                    <AdminDashboard />
                  ) : (
                    <CustomerDashboard />
                  )}
                </ProtectedRoute>
              }
            />

            <Route
              path="/support/tenant-switcher"
              element={
                <ProtectedRoute>
                  <SupportPortalRoute>
                    <SupportTenantPortal />
                  </SupportPortalRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/booking"
              element={
                <ProtectedRoute>
                  <BookingPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reservations"
              element={
                <ProtectedRoute>
                  <ReservationsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/payment/:reservationId"
              element={
                <ProtectedRoute>
                  <PaymentPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <BillingPage />
                </ProtectedRoute>
              }
            />

            {/* Protected admin routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/reservations"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminReservations />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/quotes"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminQuotes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/farm-outs"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminFarmOuts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dispatch"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDispatch />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/customers"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminCustomers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminAnalytics />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/billing"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminBilling />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/onboarding"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminOnboarding />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ai-access"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AiAccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/assistant"
              element={
                <ProtectedRoute requiredRole="admin">
                  <OperatorAssistant />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/vehicles"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminVehicles />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/drivers"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDrivers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/payments"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminPayments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/invoices"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminInvoices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/invoice-replacements"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminInvoiceReplacements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/invoice-import"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminInvoiceImport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/accounts"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminAccounts />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/accounts/:accountId"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminAccountDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/companies"
              element={
                <ProtectedRoute requiredRole="admin">
                  <PlatformOnlyRoute>
                    <AdminCompanies />
                  </PlatformOnlyRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/users"
              element={
                <ProtectedRoute requiredRole="admin">
                  <PlatformOnlyRoute>
                    <PlatformUsers />
                  </PlatformOnlyRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/support-staff"
              element={
                <ProtectedRoute requiredRole="admin">
                  <PlatformOnlyRoute>
                    <PlatformSupportStaff />
                  </PlatformOnlyRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/invoices"
              element={
                <ProtectedRoute requiredRole="admin">
                  <PlatformOnlyRoute>
                    <PlatformInvoices />
                  </PlatformOnlyRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/billing"
              element={
                <ProtectedRoute requiredRole="admin">
                  <PlatformOnlyRoute>
                    <PlatformBilling />
                  </PlatformOnlyRoute>
                </ProtectedRoute>
              }
            />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>

      <Toaster position="top-right" />
    </div>
  );
}

function PlatformOnlyRoute({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  const isPlatformHost =
    typeof window !== 'undefined' &&
    window.location?.hostname?.toLowerCase().startsWith('globaladmin.');

  if (!isPlatformHost) return <Navigate to="/" replace />;
  if (!user || !(user as any).is_platform_admin)
    return <Navigate to="/" replace />;
  return children;
}

function SupportPortalRoute({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  const isPlatformHost =
    typeof window !== 'undefined' &&
    window.location?.hostname?.toLowerCase().startsWith('globaladmin.');

  if (!isPlatformHost) return <Navigate to="/" replace />;
  if (!user || !(user as any).is_support_staff)
    return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router>
          <Routes>
            <Route
              path="/operations/*"
              element={
                <Suspense fallback={<div role="status">Opening MODAL Operations...</div>}>
                  <OperationsWorkspace />
                </Suspense>
              }
            />
            <Route
              path="*"
              element={
                <BrandingProvider>
                  <AuthProvider>
                    <StripeProvider>
                      <OperatorAssistantProvider>
                        <AppRoutes />
                        <OperatorAssistantDrawer />
                      </OperatorAssistantProvider>
                    </StripeProvider>
                  </AuthProvider>
                </BrandingProvider>
              }
            />
          </Routes>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
